/**
 * Conectores del mapa mental: las curvas padre→hijo y las relaciones punteadas.
 * Se dibujan en una pasada previa a las figuras (necesitan ambos extremos).
 */
import type { RelationShape, Shape, TopicShape } from '../../shared/types.ts'
import { displayInk, isDarkDisplay } from '../canvas/render-shape.ts'

/** Punto de salida del conector en el padre y de entrada en el hijo. */
function anchors(parent: TopicShape, child: TopicShape): { x1: number; y1: number; x2: number; y2: number } {
  const side = child.side ?? 'right'
  if (side === 'down') {
    return {
      x1: parent.x + parent.w / 2,
      y1: parent.y + parent.h,
      x2: child.x + child.w / 2,
      y2: child.y,
    }
  }
  if (side === 'left') {
    return {
      x1: parent.x,
      y1: parent.y + parent.h / 2,
      x2: child.x + child.w,
      y2: child.y + child.h / 2,
    }
  }
  return {
    x1: parent.x + parent.w,
    y1: parent.y + parent.h / 2,
    x2: child.x,
    y2: child.y + child.h / 2,
  }
}

/** Dibuja todos los conectores y relaciones visibles del tablero. */
export function drawMindMapEdges(ctx: CanvasRenderingContext2D, shapes: Shape[]): void {
  const topics = new Map<string, TopicShape>()
  for (const s of shapes) if (s.type === 'topic') topics.set(s.id, s)
  if (topics.size === 0) return

  ctx.save()
  ctx.lineCap = 'round'

  // Curvas padre → hijo, del color de la rama del hijo.
  for (const child of topics.values()) {
    if (child.hidden || !child.parentId) continue
    const parent = topics.get(child.parentId)
    if (!parent || parent.hidden) continue
    const { x1, y1, x2, y2 } = anchors(parent, child)
    ctx.strokeStyle = displayInk(child.color ?? child.branchColor)
    ctx.lineWidth = child.depth === 1 ? 2.5 : 1.8
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    if ((child.side ?? 'right') === 'down') {
      const midY = (y1 + y2) / 2
      ctx.bezierCurveTo(x1, midY, x2, midY, x2, y2)
    } else {
      const midX = (x1 + x2) / 2
      ctx.bezierCurveTo(midX, y1, midX, y2, x2, y2)
    }
    ctx.stroke()
  }

  // Relaciones: curva punteada con flecha, por encima de los conectores.
  for (const s of shapes) {
    if (s.type !== 'relation' || s.hidden) continue
    const a = topics.get(s.from)
    const b = topics.get(s.to)
    if (!a || !b || a.hidden || b.hidden) continue
    drawRelation(ctx, s, a, b)
  }
  ctx.restore()
}

/** Curva de una relación y su geometría (compartida con el hit-test). */
export function relationCurve(
  a: TopicShape,
  b: TopicShape,
): { x1: number; y1: number; cx: number; cy: number; x2: number; y2: number } {
  const x1 = a.x + a.w / 2
  const y1 = a.y + a.h / 2
  const x2 = b.x + b.w / 2
  const y2 = b.y + b.h / 2
  // El control se levanta perpendicular al segmento para que la curva "flote".
  const mx = (x1 + x2) / 2
  const my = (y1 + y2) / 2
  const dx = x2 - x1
  const dy = y2 - y1
  const len = Math.hypot(dx, dy) || 1
  const lift = Math.min(90, len * 0.25)
  return { x1, y1, cx: mx - (dy / len) * lift, cy: my + (dx / len) * lift, x2, y2 }
}

function drawRelation(
  ctx: CanvasRenderingContext2D,
  relation: RelationShape,
  a: TopicShape,
  b: TopicShape,
): void {
  const { x1, y1, cx, cy, x2, y2 } = relationCurve(a, b)
  const color = displayInk('#7A7574')
  ctx.strokeStyle = color
  ctx.lineWidth = 1.8
  ctx.setLineDash([7, 5])
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.quadraticCurveTo(cx, cy, x2, y2)
  ctx.stroke()
  ctx.setLineDash([])

  // Flecha en el destino, tangente al final de la curva.
  const tx = x2 - cx
  const ty = y2 - cy
  const tlen = Math.hypot(tx, ty) || 1
  const ux = tx / tlen
  const uy = ty / tlen
  const size = 9
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(x2, y2)
  ctx.lineTo(x2 - ux * size - uy * (size * 0.55), y2 - uy * size + ux * (size * 0.55))
  ctx.lineTo(x2 - ux * size + uy * (size * 0.55), y2 - uy * size - ux * (size * 0.55))
  ctx.closePath()
  ctx.fill()

  // Etiqueta opcional en el punto medio de la curva.
  if (relation.text) {
    const midX = (x1 + 2 * cx + x2) / 4
    const midY = (y1 + 2 * cy + y2) / 4
    ctx.font = '12px "Segoe UI", sans-serif'
    const w = ctx.measureText(relation.text).width + 12
    ctx.fillStyle = isDarkDisplay() ? '#232329' : '#FFFFFF'
    ctx.fillRect(midX - w / 2, midY - 10, w, 20)
    ctx.fillStyle = displayInk('#424242')
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(relation.text, midX, midY)
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }
}

/** Distancia de un punto a la curva de la relación (para el hit-test), muestreada. */
export function distToRelation(px: number, py: number, a: TopicShape, b: TopicShape): number {
  const { x1, y1, cx, cy, x2, y2 } = relationCurve(a, b)
  let best = Infinity
  for (let i = 0; i <= 24; i++) {
    const t = i / 24
    const inv = 1 - t
    const x = inv * inv * x1 + 2 * inv * t * cx + t * t * x2
    const y = inv * inv * y1 + 2 * inv * t * cy + t * t * y2
    best = Math.min(best, Math.hypot(px - x, py - y))
  }
  return best
}
